import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ListingCard from "@/components/listing/ListingCard";
import InstallAppBanner from "@/components/InstallAppBanner";
import SEO from "@/components/SEO";
import HeritageHero from "@/components/HeritageHero";
import MiniSlider from "@/components/MiniSlider";
import DiasporaRouteMap from "@/components/DiasporaRouteMap";
import AfricaShowcase from "@/components/AfricaShowcase";
import FeaturedMakers from "@/components/FeaturedMakers";
import NowShowing from "@/components/NowShowing";
import CountryTickerTile from "@/components/CountryTickerTile";
import TrustBar from "@/components/TrustBar";
import CategoryGrid from "@/components/CategoryGrid";
import FilmSection from "@/components/FilmSection";
import FloatingFilmChip from "@/components/FloatingFilmChip";
import TrendingCarousel from "@/components/TrendingCarousel";
import SocialProofToast from "@/components/SocialProofToast";
import { track } from "@/lib/analytics";
import { SMART_PICKS } from "@/data/smartPicks";
import { useUserLocation } from "@/hooks/useUserLocation";
import { toast } from "sonner";
import { ArrowRight, Search, Sparkles, ShieldCheck, Globe2, Heart, Crown, Flame, LifeBuoy, RefreshCw, Timer, MapPin, Lock, Shirt, Sparkle, UtensilsCrossed, Home as HomeIcon, Gift, Users, Check, HelpCircle, Navigation, Play } from "lucide-react";

const COUNTRIES = ["Nigeria", "Ghana", "South Africa", "Kenya", "Zimbabwe", "Cameroon", "Senegal", "Ethiopia"];
const PRODUCT_CATS = ["Fashion & Ankara", "Beauty & Hair", "Food & Spices", "Art & Crafts", "Jewelry"];
const SERVICE_CATS = ["Hair & Braiding", "Childminding", "Catering & Cooking", "Handyman", "Tailoring"];

const Landing = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [proPub, setProPub] = useState(null);
  const [foundersData, setFoundersData] = useState(null);
  const [trust, setTrust] = useState(null);
  const [q, setQ] = useState("");
  const { coords: userCoords } = useUserLocation();

  // Cinematic hero parallax — images drift down at ~60% scroll speed.
  // Reduced-motion users get a flat hero.
  const reducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const heroParallaxY = useTransform(scrollY, [0, 600], reducedMotion ? [0, 0] : [0, 120]);
  const heroParallaxOpacity = useTransform(scrollY, [0, 500], reducedMotion ? [1, 1] : [1, 0.55]);

  useEffect(() => {
    api.get("/listings", { params: { type: "product", featured: true, limit: 8 } })
      .then((r) => {
        if (r.data?.length) setProducts(r.data);
        else api.get("/listings", { params: { type: "product", limit: 6 } }).then((r2) => setProducts(r2.data));
      })
      .catch(() => api.get("/listings", { params: { type: "product", limit: 6 } }).then((r2) => setProducts(r2.data)));
    api.get("/listings", { params: { type: "service", limit: 4 } }).then((r) => setServices(r.data));
    api.get("/subscription/pro/public").then((r) => setProPub(r.data));
    api.get("/founders").then((r) => setFoundersData(r.data));
    api.get("/stats/trust").then((r) => setTrust(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/browse${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  const handleUseLocation = () => {
    if (userCoords) {
      // Already granted — just go straight to nearest-sorted Browse
      navigate(`/browse?sort=nearest${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      return;
    }
    if (!("geolocation" in navigator)) {
      toast.error("Your browser doesn't support location");
      return;
    }
    toast.message("Asking for your location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Store via the same hook contract so /browse picks it up automatically.
        try {
          localStorage.setItem(
            "afm_user_location_v1",
            JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() }),
          );
        } catch { /* ignore */ }
        toast.success("Showing listings near you");
        navigate(`/browse?sort=nearest${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      },
      () => toast.error("Couldn't read your location — please allow access"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  return (
    <div>
      <SEO
        title="Africa's Marketplace for Products, Services & Culture"
        description="Discover authentic African products, trusted local services and talented entrepreneurs in one marketplace. Shop African fashion, beauty, handmade crafts, home décor, groceries and book local African services near you."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Africa's Marketplace for Products, Services & Culture",
            "description": "Discover authentic African products, trusted local services and talented entrepreneurs in one marketplace.",
            "url": "https://afrimarket.org/",
            "primaryImageOfPage": "https://afrimarket.org/og-image.png",
            "inLanguage": "en",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://afrimarket.org/" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is AfriMarket?", "acceptedAnswer": { "@type": "Answer", "text": "AfriMarket is a marketplace where customers can discover African products, local services and businesses from across Africa and the diaspora." } },
              { "@type": "Question", "name": "Can I become a seller?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Businesses, creators and service providers can join AfriMarket and reach customers worldwide." } },
              { "@type": "Question", "name": "Do you offer local services?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. You can find braiders, caterers, childcare providers, photographers, event planners and other local African-owned businesses." } },
              { "@type": "Question", "name": "Are sellers verified?", "acceptedAnswer": { "@type": "Answer", "text": "We work to maintain a trusted marketplace by reviewing seller applications and encouraging customer feedback." } },
            ],
          },
        ]}
      />
      {/* Plyx coming-soon announcement — top strip, super-prominent */}
      <Link
        to="/plyx"
        data-testid="plyx-top-strip"
        className="block text-white relative overflow-hidden group"
        style={{ background: "linear-gradient(90deg, #0A0A0A 0%, #1a0a1f 50%, #0A0A0A 100%)" }}
      >
        <div className="container-narrow py-2.5 flex items-center justify-center gap-3 text-sm flex-wrap relative">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A]" style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] animate-pulse"/> New
          </span>
          <span className="font-semibold">Plyx is live</span>
          <span className="text-white/60">— TikTok-style social feed where moments become movements.</span>
          <span className="inline-flex items-center gap-1 font-semibold underline decoration-dotted underline-offset-2 group-hover:text-[#FFB42D] transition-colors">
            Watch now <ArrowRight className="w-3 h-3"/>
          </span>
        </div>
      </Link>

      {/* Legal info strip — registered company transparency (Option B) */}
      <div data-testid="legal-info-strip" className="bg-[#0F1316] text-white/85 border-b border-white/5">
        <div className="container-narrow py-1.5 flex items-center justify-center gap-2 text-[11px] sm:text-xs tracking-wide flex-wrap text-center">
          <HelpCircle className="w-3 h-3 text-white/55 shrink-0" />
          <span>Operated by <span className="font-semibold text-white">AfriMarket Group Ltd</span></span>
          <span className="text-white/35">·</span>
          <span>Company No. <span className="font-mono">17282940</span></span>
          <span className="text-white/35">·</span>
          <span>Registered in England &amp; Wales</span>
        </div>
      </div>

      {/* Founders strip */}
      {proPub && proPub.founders_remaining > 0 && (
        <Link to="/pro" data-testid="founders-strip" className="block bg-[#1A2024] text-white hover:bg-[#0F1316] transition-colors">
          <div className="container-narrow py-2.5 flex items-center justify-center gap-3 text-sm flex-wrap">
            <Flame className="w-4 h-4 text-[#D9923B]"/>
            <span className="font-semibold">Founders offer:</span>
            <span className="text-white/80">Sell with just 5% fees — £{proPub.founder_price}/30 days locked for life.</span>
            <span className="text-[#D9923B] font-semibold">Only {proPub.founders_remaining} spots left</span>
            <ArrowRight className="w-3 h-3"/>
          </div>
        </Link>
      )}

      {/* HERO — editorial cream canvas with Adinkra watermark */}
      <section className="relative overflow-hidden heritage-canvas">
        <div className="absolute inset-0 ankara-stripe opacity-30" aria-hidden />
        <div className="container-narrow relative pt-12 pb-16 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="label-overline text-[#C05A3C] mb-4" data-testid="hero-overline">AfriMarket.org · The Official Diaspora Marketplace</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-[#1A1A1A]">
              Africa&apos;s Marketplace for <em className="text-[#C05A3C] not-italic">Products</em>, <em className="text-[#2A3D36] not-italic">Services</em> &amp; Culture
            </h1>
            <p className="mt-3 font-display text-lg sm:text-xl text-[#2A3D36] italic">
              From Lagos to London. From Accra to Atlanta.
            </p>
            <p className="mt-4 text-base text-muted-foreground max-w-lg leading-relaxed font-sans-ui">
              Discover authentic African products, trusted local services and talented entrepreneurs in one marketplace.
            </p>
            <form onSubmit={submitSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl" data-testid="hero-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder='Try "Ankara dress" or "braiding in London"'
                  className="pl-12 h-14 text-base bg-white border-black/10 rounded-md"
                />
              </div>
              <Button data-testid="hero-search-btn" type="submit" className="h-14 px-8 bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md text-base">
                <Sparkles className="w-4 h-4 mr-2"/> AI Search
              </Button>
              <Button
                data-testid="hero-use-location-btn"
                type="button"
                variant="outline"
                onClick={handleUseLocation}
                className="h-14 px-5 rounded-md text-base border-[#2A3D36]/30 hover:bg-[#2A3D36]/5 text-[#2A3D36]"
                title="Find listings near me"
              >
                <Navigation className="w-4 h-4 mr-2"/> {userCoords ? "Near me" : "Use my location"}
              </Button>
            </form>
            <div className="mt-5 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/browse?type=product" data-testid="cta-shop-products">
                <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12 px-6 font-semibold">
                  Shop Products <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </Link>
              <Link to="/browse?type=service" data-testid="cta-find-services">
                <Button variant="outline" className="border-[#1A2024] text-[#1A2024] hover:bg-[#1A2024] hover:text-white rounded-md h-12 px-6 font-semibold">
                  Find Services <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </Link>
              <Link to="/register" data-testid="cta-become-seller">
                <Button variant="outline" className="border-[#2A3D36] text-[#2A3D36] hover:bg-[#2A3D36] hover:text-white rounded-md h-12 px-6 font-semibold">
                  Become a Seller
                </Button>
              </Link>
              <button
                type="button"
                data-testid="cta-watch-story"
                onClick={() => {
                  track("hero_watch_story_click");
                  document.querySelector("[data-testid=film-section]")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="group inline-flex items-center gap-2 h-12 px-5 rounded-md font-semibold text-[#1A1A1A] hover:text-[#C05A3C] transition-colors"
              >
                <span className="relative grid place-items-center w-9 h-9 rounded-full bg-[#1A1A1A] text-white group-hover:bg-[#C05A3C] transition-colors">
                  <Play className="w-3.5 h-3.5 fill-white ml-[1px]" />
                  <span className="absolute inset-0 rounded-full bg-[#C05A3C]/30 animate-ping" aria-hidden />
                </span>
                Watch our story · 60s
              </button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="label-overline text-muted-foreground mr-2">Popular:</span>
              {["Ankara", "Shea butter", "Suya spice", "Braids London", "Jollof catering"].map((t) => (
                <button key={t} onClick={() => navigate(`/browse?q=${encodeURIComponent(t)}`)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-black/10 hover:border-[#C05A3C] hover:text-[#C05A3C] transition-colors" data-testid={`hero-chip-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <motion.div className="lg:col-span-6 relative" style={{ y: heroParallaxY, opacity: heroParallaxOpacity }} data-testid="hero-parallax-images">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[560px] sm:h-[580px] lg:h-[620px]">
              <div className="col-span-4 row-span-4 rounded-lg overflow-hidden shadow-lg">
                <HeritageHero />
              </div>
              <div className="col-span-2 row-span-3 rounded-lg overflow-hidden shadow-md">
                <MiniSlider
                  alt="African services"
                  interval={7000}
                  images={[
                    { src: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?auto=format&fit=crop&q=80&w=800", alt: "Braiding service" },
                    { src: "https://images.unsplash.com/photo-1599499068127-b0e7f8b16f49?auto=format&fit=crop&q=80&w=800", alt: "Tailor at work" },
                    { src: "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?auto=format&fit=crop&q=80&w=800", alt: "Makeup artist" },
                  ]}
                />
              </div>
              <CountryTickerTile />
              <div className="col-span-2 row-span-2 rounded-lg overflow-hidden shadow-md">
                <MiniSlider
                  alt="African cuisine and food"
                  interval={8000}
                  images={[
                    { src: "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&q=80&w=800", alt: "Jollof rice" },
                    { src: "https://images.unsplash.com/photo-1604908554007-1c39d8c40034?auto=format&fit=crop&q=80&w=800", alt: "Suya skewers" },
                    { src: "https://images.unsplash.com/photo-1599047544145-c45a0e0e09a3?auto=format&fit=crop&q=80&w=800", alt: "Plantain dish" },
                  ]}
                />
              </div>
              <div className="col-span-4 row-span-2 bg-[#C05A3C] rounded-lg p-5 text-white flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/80">Earn from anywhere</div>
                  <div className="font-display text-2xl leading-tight mt-1">Start selling in 2 minutes</div>
                </div>
                <ArrowRight className="w-6 h-6"/>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PLYX TEASER — coming-soon social-commerce offshoot (moved up so it sits right after the hero) */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="plyx-teaser-section">
        <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden>
          <div className="absolute top-0 left-1/4 w-[420px] h-[420px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, #FF2D7A 0%, transparent 60%)" }}/>
          <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, #00E0C7 0%, transparent 60%)" }}/>
        </div>
        <div className="container-narrow py-20 sm:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[10px] uppercase tracking-[0.25em] text-white/70 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E0C7] animate-pulse"/> Now live · From the AfriMarket team
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
                Meet <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}>Plyx</span> — where moments become movements.
              </h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-xl font-sans-ui">
                A TikTok-style social feed built for African creators, sellers, and the diaspora. Watch a video, tap to buy, earn credit you can spend right here on AfriMarket.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/plyx"
                  data-testid="plyx-join-waitlist-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[#0A0A0A] hover:scale-[1.02] transition-transform"
                  style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
                >
                  Open Plyx
                  <ArrowRight className="w-4 h-4"/>
                </Link>
                <Link
                  to="/plyx/about"
                  data-testid="plyx-learn-more-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/10 transition-colors text-sm font-semibold"
                >
                  Learn more
                </Link>
              </div>
              <div className="mt-6 text-xs text-white/50 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><Play className="w-3 h-3"/> Short-form video</span>
                <span>·</span>
                <span>Live shopping</span>
                <span>·</span>
                <span>Watch-to-earn credit</span>
              </div>
            </div>
            {/* Decorative phone-mock for visual weight */}
            <div className="lg:col-span-5 hidden lg:block relative">
              <div className="relative mx-auto w-[280px] aspect-[9/19] rounded-[42px] border-4 border-white/15 bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] overflow-hidden shadow-[0_30px_90px_rgba(255,45,122,0.25)]">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-black"/>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Plyx</div>
                  <div className="font-display text-2xl leading-tight bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}>
                    Where moments become movements
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-2 w-full">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="aspect-[9/16] rounded-md bg-white/5 border border-white/10"/>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE — slow scrolling brand mantra */}
      <section className="relative border-y border-black/5 bg-[#FAF8F5] py-6 overflow-hidden" aria-hidden data-testid="editorial-marquee">
        <div className="marquee-mask">
          <div className="marquee-track">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center shrink-0 pr-12">
                <span className="font-display italic text-2xl sm:text-3xl text-[#2A3D36] whitespace-nowrap">From Lagos to London</span>
                <span className="mx-6 text-[#C05A3C] text-xl">✦</span>
                <span className="font-display italic text-2xl sm:text-3xl text-[#2A3D36] whitespace-nowrap">From Accra to Atlanta</span>
                <span className="mx-6 text-[#C05A3C] text-xl">✦</span>
                <span className="font-display italic text-2xl sm:text-3xl text-[#2A3D36] whitespace-nowrap">From Nairobi to New York</span>
                <span className="mx-6 text-[#C05A3C] text-xl">✦</span>
                <span className="font-display italic text-2xl sm:text-3xl text-[#2A3D36] whitespace-nowrap">From Joburg to Toronto</span>
                <span className="mx-6 text-[#C05A3C] text-xl">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP — Jumia-inspired 4-column buyer-confidence bar */}
      <TrustBar />

      {/* TRENDING NOW — curated above-the-fold carousel of best-selling picks */}
      <TrendingCarousel />

      {/* TALENT & MAKERS — moved up for prominence; always visible & recruiting */}
      <FeaturedMakers />

      {/* NOW SHOWING — Nollywood / African movie trailers strip */}
      <NowShowing />

      {/* BRAND FILM — 60s cinematic story interlude */}
      <FilmSection />

      {/* CATEGORIES GRID — Jumia-inspired one-tap navigation to top categories */}
      <CategoryGrid />

      {/* SMART PICKS — curated quick-tap discovery cards */}
      <section className="container-narrow py-12" data-testid="smart-picks-section">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <div className="label-overline text-[#C05A3C] mb-1">Smart picks</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">One tap. One hyper-local search.</h2>
            <p className="text-sm text-muted-foreground mt-1 font-sans-ui max-w-md">Size, colour, city, currency — all locked in for you.</p>
          </div>
          <Link to="/browse?type=product" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium hover:text-[#C05A3C]" data-testid="smart-picks-see-all">
            Browse everything <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory" data-testid="smart-picks-row">
          {SMART_PICKS.map((p) => (
            <Link
              key={p.id}
              to={p.href}
              data-testid={`smart-pick-${p.id}`}
              className="snap-start flex-shrink-0 w-[240px] sm:w-[260px] rounded-xl border border-black/10 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
              style={{ backgroundColor: p.bg }}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: p.accent }}>{p.overline}</div>
              <div className="font-display text-xl mt-1 leading-tight" style={{ color: "#1A2024" }}>{p.title}</div>
              <div className="text-xs text-muted-foreground font-sans-ui mt-2">{p.sub}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: p.accent }}>
                Open this search <ArrowRight className="w-3 h-3"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DISCOVER AFRICA — country-tabbed cultural product gallery */}
      <AfricaShowcase />


      {/* DIASPORA ROUTE MAP — animated visual storytelling */}
      <section className="heritage-canvas py-16 sm:py-20 border-y border-black/5" data-testid="diaspora-section">
        <div className="container-narrow relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="label-overline text-[#C05A3C] mb-3">A bridge between worlds</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#1A2024]">
                Africa, woven into <em className="text-[#C05A3C] not-italic">every corner</em> of the diaspora.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans-ui max-w-md">
                Whether you&apos;re in Brixton, Brooklyn or Brampton — your favourite spices, fabrics, salons and tailors are now a tap away. AfriMarket is the thread connecting Africa&apos;s heritage to its global family.
              </p>
              <div className="mt-7 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#C05A3C]"/>
                  <span className="text-[#1A2024] font-medium">14+ African origins</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#2A3D36]"/>
                  <span className="text-[#1A2024] font-medium">UK · US · Canada hubs</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <DiasporaRouteMap />
            </div>
          </div>
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="container-narrow py-20" data-testid="categories-section">
        <div className="mb-10">
          <div className="label-overline text-[#C05A3C] mb-2">Shop by Category</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Everything African, in one place</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { name: "African Fashion", icon: Shirt, color: "#C05A3C", to: "/african-fashion",
              desc: "Discover Ankara, Dashiki, Kente, jewellery and contemporary African fashion from trusted sellers." },
            { name: "Beauty & Hair", icon: Sparkle, color: "#D9923B", to: "/african-beauty",
              desc: "Shop shea butter, black soap, hair products, wigs, braiding accessories and natural skincare." },
            { name: "Food & Groceries", icon: UtensilsCrossed, color: "#4A5D23", to: "/african-food",
              desc: "Buy authentic African ingredients, spices, snacks, drinks and pantry essentials." },
            { name: "Home & Living", icon: HomeIcon, color: "#2A3D36", to: "/african-home",
              desc: "Explore African-inspired décor, artwork, furniture and handmade crafts." },
            { name: "Gifts & Culture", icon: Gift, color: "#B84A28", to: "/african-gifts",
              desc: "Find meaningful gifts, books, cultural products and unique handmade items." },
            { name: "Local Services", icon: Users, color: "#1A2024", to: "/african-services",
              desc: "Connect with trusted African caterers, braiders, childcare providers, photographers and event professionals." },
          ].map((cat) => (
            <Link
              key={cat.name}
              to={cat.to}
              data-testid={`category-${cat.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="group bg-white border border-black/10 rounded-2xl p-6 hover:border-[#C05A3C] hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: `${cat.color}15` }}>
                <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
              </div>
              <h3 className="font-display text-xl mb-2 group-hover:text-[#C05A3C] transition-colors">{cat.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{cat.desc}</p>
              <div className="mt-4 text-sm font-medium text-[#C05A3C] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Browse <ArrowRight className="w-3 h-3"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BUYER PROTECTION / TRUST PROOF */}
      {trust && trust.total_cases > 0 && (
        <section className="bg-[#FBF6EE]" data-testid="trust-section">
          <div className="container-narrow py-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="label-overline text-[#C05A3C] mb-3">Buyer protection</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
                Every order backed by a real human.
              </h2>
              <p className="text-muted-foreground mt-4 font-sans-ui leading-relaxed max-w-md">
                Open a case from your dashboard and we&apos;ll hold the seller&apos;s payout
                while we investigate. Refunds, warnings, suspensions — handled by a person,
                not a bot.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/complaints" data-testid="trust-open-case-btn">
                  <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md">
                    <LifeBuoy className="w-4 h-4 mr-2"/> Open a case
                  </Button>
                </Link>
                <Link to="/buyer-guarantee" className="inline-flex items-center text-sm font-medium hover:text-[#C05A3C]" data-testid="trust-guarantee-link">
                  How buyer protection works <ArrowRight className="w-4 h-4 ml-1"/>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-6 border border-black/5" data-testid="trust-tile-resolution">
                <ShieldCheck className="w-5 h-5 text-[#4A5D23] mb-3"/>
                <div className="font-display text-4xl leading-none">{trust.resolution_rate_pct}%</div>
                <div className="text-xs text-muted-foreground mt-2">cases resolved or closed · {trust.resolved_cases}/{trust.total_cases}</div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-black/5" data-testid="trust-tile-refunds">
                <RefreshCw className="w-5 h-5 text-[#C05A3C] mb-3"/>
                <div className="font-display text-4xl leading-none">{trust.refunds_issued}</div>
                <div className="text-xs text-muted-foreground mt-2">refunds issued to buyers</div>
              </div>
              <div className="bg-[#1A2024] text-white rounded-2xl p-6 border border-transparent" data-testid="trust-tile-time">
                <Timer className="w-5 h-5 text-[#D9923B] mb-3"/>
                <div className="font-display text-4xl leading-none">
                  {trust.avg_resolution_hours != null ? `${trust.avg_resolution_hours}h` : "—"}
                </div>
                <div className="text-xs text-white/70 mt-2">average case-to-close time</div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-black/5" data-testid="trust-tile-removed">
                <Sparkles className="w-5 h-5 text-[#C05A3C] mb-3"/>
                <div className="font-display text-4xl leading-none">{trust.listings_removed + trust.users_suspended}</div>
                <div className="text-xs text-muted-foreground mt-2">bad-actor listings &amp; accounts removed</div>
              </div>
            </div>
          </div>
        </section>
      )}

      <InstallAppBanner />

      {/* PRODUCTS */}
      <section className="container-narrow py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-overline text-[#C05A3C] mb-2">Authentic. Handmade. African.</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Featured African pieces</h2>
          </div>
          <Link to="/browse?type=product" className="hidden sm:flex items-center gap-1 text-sm font-medium hover:text-[#C05A3C]" data-testid="see-all-products">
            See all <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="mb-6 flex flex-wrap gap-2">
          {PRODUCT_CATS.map((c) => (
            <Link key={c} to={`/browse?type=product&category=${encodeURIComponent(c)}`} className="text-xs px-3 py-1.5 rounded-full bg-[#F3EFE9] hover:bg-[#C05A3C] hover:text-white transition-colors">{c}</Link>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
          {products.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
        </div>
      </section>

      {/* SERVICES FEATURE BAND */}
      <section className="bg-[#1A2024] text-white">
        <div className="container-narrow py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 flex flex-col justify-between">
            <div>
              <div className="label-overline text-[#D9923B] mb-3">Local services, real community</div>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight">
                Need a braider in London? A jollof caterer in Toronto?
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed font-sans-ui">
                African service providers near you — vetted, reviewed and reachable in one tap.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {SERVICE_CATS.map((c) => (
                <Link key={c} to={`/browse?type=service&category=${encodeURIComponent(c)}`} className="text-xs px-3 py-1.5 rounded-full border border-white/20 hover:bg-white hover:text-[#1A2024] transition-colors">{c}</Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {services.map((l) => (
              <Link key={l.listing_id} to={`/listing/${l.listing_id}`} data-testid={`service-card-${l.listing_id}`} className="group bg-white text-[#1A1A1A] rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-[5/3] overflow-hidden">
                  <img src={l.images?.[0]} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                </div>
                <div className="p-5">
                  <Badge className="bg-[#F3EFE9] text-[#2A3D36] mb-2">{l.category}</Badge>
                  <h3 className="font-display text-xl">{l.title}</h3>
                  <div className="text-xs text-muted-foreground mt-1">{l.city}, {l.country}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRY SHOWCASE */}
      <section className="container-narrow py-20">
        <div className="label-overline text-[#C05A3C] mb-2">Shop by country</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-8">Discover the continent, one piece at a time</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {COUNTRIES.map((c) => (
            <Link key={c} to={`/browse?country=${encodeURIComponent(c)}`} className="bg-white border border-black/10 rounded-lg p-6 hover:border-[#C05A3C] hover:shadow-md transition-all group" data-testid={`country-${c}`}>
              <div className="text-3xl font-display text-[#1A1A1A] group-hover:text-[#C05A3C]">{c.slice(0,2)}</div>
              <div className="mt-2 text-sm font-medium">{c}</div>
              <div className="text-xs text-muted-foreground">Shop now <ArrowRight className="inline w-3 h-3 ml-1"/></div>
            </Link>
          ))}
        </div>
      </section>

      {/* FOUNDERS LEADERBOARD */}
      <section className="bg-[#FAF8F5] border-t border-black/5">
        <div className="container-narrow py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="label-overline text-[#D9923B] mb-2 flex items-center gap-2"><Crown className="w-3.5 h-3.5"/> Pro Founders</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Meet the {foundersData?.count || ""} sellers who got here first.</h2>
              <p className="text-muted-foreground mt-2 max-w-xl font-sans-ui">
                Pro Founders locked in £{proPub?.founder_price || 4.99} / 30 days for life. Their listings get priority placement and a ✦ Pro mark forever.
              </p>
            </div>
            {proPub && proPub.founders_remaining > 0 && (
              <Link to="/pro" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#1A2024] text-white hover:bg-[#0F1316] transition-colors" data-testid="founders-section-cta">
                <Flame className="w-4 h-4 text-[#D9923B]"/>
                <span className="font-semibold">{proPub.founders_remaining} spots left</span>
                <ArrowRight className="w-4 h-4"/>
              </Link>
            )}
          </div>

          {foundersData?.founders?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="founders-grid">
              {foundersData.founders.map((f, i) => (
                <div key={f.user_id} className="group relative bg-white rounded-lg overflow-hidden border border-black/10 hover:border-[#D9923B] transition-colors" data-testid={`founder-${f.user_id}`}>
                  <div className="absolute top-2 left-2 z-10">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1A2024] text-[#D9923B] text-[10px] font-semibold">
                      <Crown className="w-2.5 h-2.5"/> #{i + 1}
                    </span>
                  </div>
                  <div className="aspect-square overflow-hidden bg-[#F3EFE9]">
                    {f.picture ? (
                      <img src={f.picture} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-3xl text-[#C05A3C] bg-[#F3EFE9]">
                        {f.name?.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-sm leading-tight truncate">{f.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{f.country}</div>
                    {f.bio && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-1">{f.bio}</div>}
                  </div>
                </div>
              ))}
              {/* Empty slots if fewer than the row needs */}
              {Array.from({ length: Math.max(0, 6 - foundersData.founders.length) }).map((_, i) => (
                <Link to="/pro" key={`empty-${i}`} className="aspect-square sm:aspect-auto sm:min-h-[200px] rounded-lg border-2 border-dashed border-[#D9923B]/40 flex items-center justify-center p-3 text-center hover:border-[#D9923B] hover:bg-[#D9923B]/5 transition-colors" data-testid={`founder-empty-${i}`}>
                  <div>
                    <Crown className="w-5 h-5 mx-auto text-[#D9923B] mb-2"/>
                    <div className="text-xs font-semibold text-[#1A2024]">Your spot?</div>
                    <div className="text-[10px] text-muted-foreground mt-1">Claim it →</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-[#D9923B]/40 p-10 text-center" data-testid="founders-empty">
              <Crown className="w-10 h-10 mx-auto text-[#D9923B] mb-3"/>
              <h3 className="font-display text-2xl">Be the first founder.</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">100 spots, locked at £{proPub?.founder_price || 4.99} for life. The next sellers to upgrade get their face here.</p>
              <Button onClick={() => navigate("/pro")} className="mt-5 bg-[#1A2024] hover:bg-[#0F1316] text-white rounded-md" data-testid="founders-empty-cta">Claim founder spot</Button>
            </div>
          )}
        </div>
      </section>

      {/* WHY AFRIMARKET */}
      <section className="bg-white border-t border-black/5" data-testid="why-section">
        <div className="container-narrow py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="label-overline text-[#C05A3C] mb-3">Why AfriMarket?</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Built for African businesses, beloved by the diaspora.
            </h2>
            <p className="text-muted-foreground mt-5 font-sans-ui leading-relaxed">
              AfriMarket connects African businesses with customers around the world. Our platform helps you discover authentic products, trusted services and unique cultural experiences while supporting African entrepreneurs.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="label-overline text-[#2A3D36] mb-4">What makes us different?</div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {[
                "Authentic African products",
                "Trusted local services",
                "Verified sellers",
                "Secure transactions",
                "Community-focused marketplace",
                "Global reach with local connections",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 bg-[#FBF6EE] rounded-lg p-4">
                  <div className="w-6 h-6 rounded-full bg-[#4A5D23] text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5"/>
                  </div>
                  <span className="text-sm font-medium leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FBF6EE] border-t border-black/5" data-testid="faq-section">
        <div className="container-narrow py-20 max-w-3xl">
          <div className="text-center mb-12">
            <div className="label-overline text-[#C05A3C] mb-3 flex items-center justify-center gap-2">
              <HelpCircle className="w-3.5 h-3.5"/> FAQ
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "What is AfriMarket?",
                a: "AfriMarket is a marketplace where customers can discover African products, local services and businesses from across Africa and the diaspora.",
              },
              {
                q: "Can I become a seller?",
                a: "Yes. Businesses, creators and service providers can join AfriMarket and reach customers worldwide.",
              },
              {
                q: "Do you offer local services?",
                a: "Yes. You can find braiders, caterers, childcare providers, photographers, event planners and other local African-owned businesses.",
              },
              {
                q: "Are sellers verified?",
                a: "We work to maintain a trusted marketplace by reviewing seller applications and encouraging customer feedback.",
              },
            ].map((item, idx) => (
              <details
                key={item.q}
                data-testid={`faq-item-${idx}`}
                className="group bg-white rounded-2xl border border-black/10 p-6 hover:border-[#C05A3C] transition-colors"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between font-display text-lg font-semibold">
                  {item.q}
                  <ArrowRight className="w-4 h-4 text-[#C05A3C] transition-transform group-open:rotate-90"/>
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed font-sans-ui">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow pb-24">
        <div className="bg-[#C05A3C] text-white rounded-lg p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute inset-0 ankara-stripe opacity-40" aria-hidden />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="label-overline text-white/80 mb-3 flex items-center gap-2"><Crown className="w-3.5 h-3.5"/> {proPub?.founders_remaining > 0 ? `Founders offer · ${proPub.founders_remaining} spots left` : "Pro membership"}</div>
              <h2 className="font-display text-4xl lg:text-5xl leading-tight">{proPub?.founders_remaining > 0 ? `Sell with 5% fees — £${proPub.founder_price}/30 days, locked for life.` : "Bring your craft, food, fabric or skill to the world."}</h2>
            </div>
            <div className="md:text-right">
              <p className="text-white/90 mb-6 max-w-md md:ml-auto font-sans-ui leading-relaxed">
                {proPub?.founders_remaining > 0
                  ? "Be one of AfriMarket's founding sellers. Lower fees, verified badge and priority placement — locked at the founder rate forever."
                  : "List in minutes with our AI description helper. Reach diaspora customers across UK, US and Canada."}
              </p>
              <Button data-testid="cta-start-selling" onClick={() => navigate(proPub?.founders_remaining > 0 ? "/pro" : "/register")} className="bg-white text-[#C05A3C] hover:bg-white/90 rounded-md h-12 px-8 font-semibold">
                {proPub?.founders_remaining > 0 ? "Claim a founder spot" : "Start selling free"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Floating film chip — small intermittent video player bottom-right */}
      <FloatingFilmChip enabled />
      <SocialProofToast />
    </div>
  );
};

export default Landing;
